import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import { apiClient } from "../../../../../globals/utils/api";

const humanizeKey = (key) =>
  key
    .replace(/_/g, " ")
    .replace(/\bid\b/i, "ID")
    .replace(/\b\w/g, (c) => c.toUpperCase());

/** Normalize axios / DRF / Django HTML error payloads for display. */
const formatApiError = (err) => {
  if (err == null) return "Request failed";
  if (typeof err === "string") {
    if (err.includes("Server Error") || err.includes("<!DOCTYPE") || err.includes("<html")) {
      return (
        "Server error (500). The API returned an HTML error page — check the Django " +
        "server logs on the environment you are calling. Common causes: missing table " +
        "Vermost_Account_Merge_Log, a SQL error during merge, or an unhandled exception in " +
        "account_merge."
      );
    }
    return err;
  }
  if (Array.isArray(err.detail)) {
    return err.detail
      .map((d) => (typeof d === "string" ? d : d?.msg || JSON.stringify(d)))
      .join("; ");
  }
  if (err.detail != null) return String(err.detail);
  if (err.message) return String(err.message);
  return "Request failed";
};

/**
 * Modal dialog shown before a merge runs. Fetches `/duplicate-accounts/preview`
 * to display the exact counts of bonds / mappings / records that will move
 * from the loser onto the keeper, then a Confirm button kicks off the actual
 * merge via `/duplicate-accounts/merge`.
 */
const MergePreviewDialog = ({
  open,
  onClose,
  mergeType,
  keeperId,
  loserId,
  onMerged,
}) => {
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [submitError, setSubmitError] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!open || !keeperId || !loserId) return undefined;
    setLoading(true);
    setPreview(null);
    setLoadError(null);
    setSubmitError(null);
    apiClient(
      "/duplicate-accounts/preview",
      "POST",
      { merge_type: mergeType, keeper_id: keeperId, loser_id: loserId }
    )
      .then((data) => {
        if (!cancelled) setPreview(data);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(formatApiError(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, mergeType, keeperId, loserId]);

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      const result = await apiClient(
        "/duplicate-accounts/merge",
        "POST",
        { merge_type: mergeType, keeper_id: keeperId, loser_id: loserId }
      );
      if (onMerged) onMerged(result);
      onClose();
    } catch (err) {
      setSubmitError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const counts = preview?.counts || {};
  const totalCounts = Object.values(counts).reduce((a, b) => a + (Number(b) || 0), 0);
  const canConfirm = !!preview && !loading && !submitting;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>
        Confirm Merge
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={120}>
            <CircularProgress />
          </Box>
        ) : loadError ? (
          <Alert severity="error">{loadError}</Alert>
        ) : preview ? (
          <Box>
            {submitError ? (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSubmitError(null)}>
                {submitError}
              </Alert>
            ) : null}
            <Alert severity="warning" sx={{ mb: 2 }}>
              This will reassign every reference from the <strong>loser</strong> account to the
              <strong> keeper</strong> account, then soft-delete the loser. The change is reversible
              within 30 days from the Merge Log tab.
            </Alert>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2, mb: 2 }}>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>KEEPER</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{preview.keeper}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: "text.secondary" }}>LOSER (will be soft-deleted)</Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>{preview.loser}</Typography>
              </Box>
            </Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Records that will move ({totalCounts})
            </Typography>
            {totalCounts === 0 ? (
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                No related records found — only the loser account itself will be soft-deleted.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
                {Object.entries(counts)
                  .filter(([, v]) => Number(v) > 0)
                  .map(([key, value]) => (
                    <Chip
                      key={key}
                      label={`${humanizeKey(key)}: ${value}`}
                      size="small"
                      variant="outlined"
                      sx={{ mb: 0.5 }}
                    />
                  ))}
              </Stack>
            )}
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          disabled={!canConfirm}
        >
          {submitting ? <><CircularProgress size={16} sx={{ mr: 1 }} /> Merging…</> : "Confirm Merge"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergePreviewDialog;
